import { Helmet } from "react-helmet-async";
import { SITE } from "@/lib/seo";
import { webPageSchema } from "@/lib/schemas";

/**
 * SEO — per-page meta tag + JSON-LD injector.
 *
 * Props:
 *   title         page title (without firm suffix)
 *   description   meta description (~155 chars)
 *   path          page path, e.g. "/locations/austin" — used to build canonical
 *   ogImage       optional OG/Twitter image URL
 *   ogType        defaults to "website"; "article" for journal posts
 *   noindex       set true on staging/duplicate pages
 *   schema        a single JSON-LD object, or an array of them
 */
export default function SEO({
  title,
  description,
  keywords,
  path = "/",
  ogImage,
  ogImageWidth = 1200,
  ogImageHeight = 630,
  ogImageAlt,
  ogType = "website",
  noindex = false,
  schema = null,
}) {
  const fullTitle = title
    ? (title.includes(SITE.name) ? title : `${title} · ${SITE.name}`)
    : `${SITE.name} — ${SITE.tagline}`;
  const desc = description || SITE.description;
  const canonical = `${SITE.url}${path === "/" ? "" : path}`;
  const image = ogImage || SITE.ogImage;
  const userSchemas = !schema ? [] : Array.isArray(schema) ? schema : [schema];
  // Per-page keywords override the default site-wide list when supplied.
  const metaKeywords = keywords
    ? keywords
    : SITE.keywords
    ? SITE.keywords.join(", ")
    : null;
  // Every page automatically gets a WebPage node — sets `dateModified`,
  // `primaryImageOfPage`, `speakable`, and ties the URL to the firm @id.
  const webPage = webPageSchema({ path, title: fullTitle, description: desc });
  const schemas = [webPage, ...userSchemas];

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {metaKeywords && <meta name="keywords" content={metaKeywords} />}
      <meta name="author" content={SITE.founder} />
      <meta name="publisher" content={SITE.legalName} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {!noindex && <meta name="robots" content="index,follow,max-image-preview:large" />}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={SITE.locale} />
      <meta property="og:image" content={image} />
      <meta property="og:image:secure_url" content={image} />
      <meta property="og:image:width" content={String(ogImageWidth)} />
      <meta property="og:image:height" content={String(ogImageHeight)} />
      {ogImageAlt && <meta property="og:image:alt" content={ogImageAlt} />}
      <meta property="og:updated_time" content={SITE.lastUpdated} />

      {/* Preconnect for image CDN to shave LCP */}
      <link rel="preconnect" href="https://images.unsplash.com" crossOrigin="" />
      <link rel="dns-prefetch" href="https://images.unsplash.com" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />
      {SITE.twitter && <meta name="twitter:site" content={SITE.twitter} />}

      {/* JSON-LD */}
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}
